/**
 * Configuration model for the timeslot calendar event card and its hover detail popover.
 *
 * The shape here is deliberately a plain, JSON-serialisable DTO: today it is persisted to
 * localStorage (see `repository.ts`), but it is meant to be moved to the backend unchanged, so it
 * carries a `version` for migrations and uses stable string keys a BE enum can mirror 1:1.
 */

/**
 * Stable fragment identifiers.
 *
 * The values of the fragments that already existed keep their legacy `CFR*` spelling so the
 * company custom-data payload (`CALENDAR_CARD_FRAGMENTS`, a comma-separated list of these) keeps
 * resolving without a data migration.
 */
export enum CalendarFragmentKey {
  ApplicationId = 'CFRApplicationId',
  WorkflowIcon = 'CFRWorkflowIcon',
  Name = 'CFRName',
  /** The on-behalf-of company, falling back to the reference when there is none. See `CardTitle`. */
  Title = 'CFRTitle',
  OnBehalfOf = 'CFROnBehalfOf',
  Flags = 'CFRFlags',
  ActionTypes = 'CFRActionTypes',
  Labels = 'CFRLabels',
  CarrierInfo = 'CFRCarrierInfo',
  CargoItem = 'CFRCargoItem',
  Created = 'CFRCreated',
  Updated = 'CFRUpdated',
  SlotDuration = 'CFRSlotDuration',
  WorkflowState = 'CFRWorkflowState',
  Location = 'CFRLocation',
  Duration = 'CFRDuration',
  TimeRange = 'CFRTimeRange',
  CustomerName = 'CFRCustomerName',
  Driver = 'CFRDriver',
  OrderNote = 'CFROrderNote',
  CargoWeight = 'CFRCargoWeight',
  CargoVolume = 'CFRCargoVolume',
  LinkedExpedition = 'CFRLinkedExpedition',
  LinkedSupplierNotification = 'CFRLinkedSupplierNotification',
}

const ALL_FRAGMENT_KEYS: CalendarFragmentKey[] = Object.values(CalendarFragmentKey);

export const isCalendarFragmentKey = (value: unknown): value is CalendarFragmentKey =>
  typeof value === 'string' && ALL_FRAGMENT_KEYS.includes(value as CalendarFragmentKey);

/**
 * Slots a fragment can be placed into. Both surfaces share the enum; each one declares the subset
 * it actually renders (`CARD_ZONES` / `DETAIL_ZONES`), so a fragment can never land in a zone the
 * surface has no container for.
 */
export enum LayoutZone {
  /** Title side of the header row — an inline flow, so fragments sit next to each other. */
  HeaderLeft = 'HEADER_LEFT',
  /** Trailing side of the header row, right-aligned. */
  HeaderRight = 'HEADER_RIGHT',
  /** Main stack: one fragment per row, in configured order. */
  Body = 'BODY',
}

export const CARD_ZONES: LayoutZone[] = [LayoutZone.HeaderLeft, LayoutZone.HeaderRight, LayoutZone.Body];

export const DETAIL_ZONES: LayoutZone[] = [LayoutZone.HeaderLeft, LayoutZone.HeaderRight, LayoutZone.Body];

export type CalendarSurface = 'card' | 'detail';

export const zonesForSurface = (surface: CalendarSurface): LayoutZone[] => (surface === 'card' ? CARD_ZONES : DETAIL_ZONES);

/** One placed fragment. Anything beyond presence/order is intentionally minimal — typography, colours and value formatting come from the design, not from configuration. */
export interface FragmentConfig {
  key: CalendarFragmentKey;
  /** Only honoured by fragments whose definition sets `supportsIcon`; defaults to true. */
  showIcon?: boolean;
}

export type SurfaceLayout = Partial<Record<LayoutZone, FragmentConfig[]>>;

export interface CalendarLayoutConfig {
  version: number;
  updatedAt: string;
  card: SurfaceLayout;
  detail: SurfaceLayout;
}

export const CALENDAR_LAYOUT_VERSION = 1;

export const zoneItems = (layout: SurfaceLayout | undefined, zone: LayoutZone): FragmentConfig[] => layout?.[zone] ?? [];
